import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Inbox, LoaderCircle, RefreshCw } from 'lucide-react';

export function LoadingState({ label = 'Loading…', compact = false }) {
  return (
    <div className={`async-state ${compact ? 'async-state--compact' : ''}`} role="status">
      <LoaderCircle className="spin" size={24} />
      <p>{label}</p>
    </div>
  );
}

export function ErrorState({ message, onRetry }) {
  // A retry that already failed once is being pressed because the network is slow or down,
  // which is exactly when it takes longest to answer and looks most like nothing happened.
  // The latch is a ref so a second press in the same tick is dropped; `retrying` only drives
  // the label. Every call site returns the promise from its loader, so this settles honestly.
  const runningRef = useRef(false);
  const mountedRef = useRef(true);
  const [retrying, setRetrying] = useState(false);

  const retry = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    setRetrying(true);
    try {
      await onRetry();
    } finally {
      runningRef.current = false;
      // The successful path unmounts this component, so guard the state write.
      if (mountedRef.current) setRetrying(false);
    }
  }, [onRetry]);

  useEffect(() => () => { mountedRef.current = false; }, []);

  return (
    <div className="async-state async-state--error" role="alert">
      <AlertTriangle size={28} />
      <h3>We couldn’t load this</h3>
      <p>{message}</p>
      {onRetry ? (
        <button
          type="button"
          className="button button--secondary"
          onClick={retry}
          disabled={retrying}
          aria-busy={retrying || undefined}
        >
          <RefreshCw size={16} className={retrying ? 'spin' : undefined} />
          {retrying ? ' Retrying…' : ' Try again'}
        </button>
      ) : null}
    </div>
  );
}

export function EmptyState({ title = 'Nothing here yet', message, action }) {
  return (
    <div className="async-state async-state--empty">
      <span className="empty-icon"><Inbox size={28} /></span>
      <h3>{title}</h3>
      {message ? <p>{message}</p> : null}
      {action || null}
    </div>
  );
}
